import { createStyles, makeStyles } from "@material-ui/core";
import { format } from "date-fns";
import * as React from "react";

import {
    AttachmentLink,
    baseColors,
    Divider,
    LabeledPageActionButton,
    MobilePage,
    MobilePageActions,
    MobilePageSection,
    RelodeTheme,
    Text,
    textWithLinks,
} from "../../../../";
import {
    EnvelopeIcon,
    EnvelopeOpenIcon,
    ForwardIcon,
    ReplyIcon,
} from "../../../../assets/icons";
import { MessageDetailsProps } from "./messageDetailsProps";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            container: {
                display: "flex",
                flexDirection: "column",
            },
            actions: {
                display: "flex",
                justifyContent: "space-evenly",
                flex: 1,
            },
            headerText: {
                lineHeight: "unset",
                padding: 0,
            },
            backButton: {
                marginBottom: 0,
            },
            label: {
                color: theme.relode.colors.label.main,
            },
            detailsContainer: {
                display: "flex",
                flexDirection: "row",
                flexGrow: 1,
                margin: theme.spacing(2, 0),
            },
            avatar: {
                height: 30,
                width: 30,
                margin: theme.spacing(0, 2),
                backgroundColor: baseColors.warm_gray,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            },
            messageBody: {
                padding: theme.spacing(2),
                height: "100%",
                width: "100%",
                flex: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: theme.typography.fontSize,
            },
            attachments: {
                margin: theme.spacing(2),
                display: "flex",
                flexDirection: "column",
            },
        }),
    { name: "MessageDetailMobile", index: 10 }
);

export const MessageDetailMobile = (props: MessageDetailsProps) => {
    const {
        backUrl,
        handleStatus,
        handleComposerOpen,
        currentMessage,
        loadingMessage,
        title,
    } = props;
    const classes = useStyles({});
    const recipientsDiff =
        currentMessage?.totalRecipientsCount -
        currentMessage?.recipients?.length;

    const getLabel = React.useMemo((): string => {
        if (!currentMessage) return null;

        if (!!currentMessage.leadName && !!currentMessage.jobName)
            return `${currentMessage.leadName} - ${currentMessage.jobName} - ${currentMessage.jobLocationName}`;

        if (!!currentMessage.jobName)
            return `${currentMessage.jobName} - ${currentMessage.jobLocationName}`;
        return currentMessage.leadName;
    }, [currentMessage]);

    const renderFooterActions = (selectedSection: string) => {
        if (!currentMessage) return null;

        return (
            <div className={classes.actions}>
                {currentMessage.canChangeMessageStatus ? (
                    <>
                        {currentMessage.isRead ? (
                            <LabeledPageActionButton
                                id="markMessageUnread"
                                onClick={() => handleStatus("unread")}
                                color="primary"
                                title="mark as unread"
                                label="mark unread"
                            >
                                <EnvelopeIcon />
                            </LabeledPageActionButton>
                        ) : (
                            <LabeledPageActionButton
                                id="markMessageRead"
                                onClick={() => handleStatus("read")}
                                color="primary"
                                title="mark as read"
                                label="mark read"
                            >
                                <EnvelopeOpenIcon />
                            </LabeledPageActionButton>
                        )}
                    </>
                ) : null}

                {currentMessage.canReply ? (
                    <LabeledPageActionButton
                        id="replyMessage"
                        onClick={() => handleComposerOpen("reply")}
                        color="primary"
                        title="reply"
                        label="reply"
                    >
                        <ReplyIcon />
                    </LabeledPageActionButton>
                ) : null}
                <LabeledPageActionButton
                    id="forwardMessage"
                    onClick={() => handleComposerOpen("forward")}
                    color="primary"
                    title="forward"
                    label="forward"
                >
                    <ForwardIcon />
                </LabeledPageActionButton>
            </div>
        );
    };

    return (
        <MobilePage
            pageTitle={getLabel}
            isBusy={loadingMessage}
            defaultSection="messageDetails"
            actions={
                <MobilePageActions
                    classes={{
                        headerText: classes.headerText,
                        backButton: classes.backButton,
                    }}
                    title={title}
                    backUrl={backUrl}
                ></MobilePageActions>
            }
            footerActions={renderFooterActions}
        >
            {!!currentMessage ? (
                // NOTE: this breaks with multiple sections as the MobilePage doesn't detect its children with a <></> around them
                <MobilePageSection id="messageDetails">
                    <div className={classes.container}>
                        <div className={classes.detailsContainer}>
                            <div className={classes.avatar}>
                                <Text variant="body2" color="primary">
                                    {currentMessage.sender.initials}
                                </Text>
                            </div>
                            <div>
                                <Text id="messageDetailsSender" variant="body1">
                                    {currentMessage.sender.displayName}
                                </Text>
                                <Text variant="body2" className={classes.label}>
                                    {"to: "}
                                    {currentMessage.recipients.map(
                                        (recipient, i) => (
                                            <span key={i}>
                                                {recipient.displayName}
                                                {currentMessage.recipients
                                                    .length -
                                                    1 >
                                                i
                                                    ? ", "
                                                    : null}
                                            </span>
                                        )
                                    )}
                                    {recipientsDiff > 0
                                        ? ` and ${recipientsDiff} others`
                                        : null}
                                </Text>
                                <Text
                                    id="messageDetailsDateSent"
                                    variant="body2"
                                    className={classes.label}
                                >
                                    {format(
                                        new Date(
                                            currentMessage.createTimestamp
                                        ),
                                        "P p"
                                    )}
                                </Text>
                            </div>
                        </div>
                        <Divider></Divider>
                        {currentMessage.attachments &&
                        currentMessage.attachments.length > 0 ? (
                            <>
                                <div className={classes.attachments}>
                                    {currentMessage.attachments.map(
                                        (att, i) => (
                                            <AttachmentLink
                                                baseId="messagesDetails"
                                                key={i}
                                                icon={true}
                                                url={att.fileUri}
                                                playMediaInNewWindow={true}
                                                label={att.fileName}
                                                type={att.contentType}
                                                highlightType="internal"
                                            />
                                        )
                                    )}
                                </div>
                                <Divider></Divider>
                            </>
                        ) : null}

                        <div className={classes.messageBody}>
                            {currentMessage.isHtml === true ? (
                                <iframe
                                    key={currentMessage.messageId}
                                    title={currentMessage.messageId.toString()}
                                    srcDoc={currentMessage.body}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        margin: 0,
                                    }}
                                />
                            ) : (
                                textWithLinks(currentMessage.body)
                            )}
                        </div>
                    </div>
                </MobilePageSection>
            ) : null}
        </MobilePage>
    );
};
